import React, { useEffect, useState } from "react";
import { matchPath } from "react-router-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSnapshot } from "valtio";
import { authStore } from "../../stores/authStore";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import { Sidebar } from "../../components/ui/sidebar";
import {
  BookOpen,
  Bookmark,
  FileText,
  Settings,
  ChevronDown,
  UserCircle2Icon,
  LogOut,
  HomeIcon,
} from "lucide-react";
import SessionExpiryModal from "../../components/dashboard/SessionExpiryModal";
import ProfileCreationForm from "../../components/dashboard/ProfileCompletionForm";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import { NotificationDropdown } from "../../components/ui/notifications";
import { CURRENT_COURSE_NAME_KEY } from "../../utils/localStoragekeys";
import { userDataFormat } from "@/utils/userDataFormat";
import logo from "@/assets/logo.svg";
import sponsors from "@/assets/sponsors.svg";
import powered_colored from "@/assets/powered_colored.svg";

const sidebarItems = [
  {
    name: "Courses",
    href: "/dashboard",
    icon: <BookOpen className="w-5 h-5" />,
  },
  {
    name: "Your Progress",
    href: "/dashboard/progress",
    icon: <FileText className="w-5 h-5" />,
  },
  {
    name: "Contact Support",
    href: "/dashboard/tickets",
    icon: <Bookmark className="w-5 h-5" />,
  },
];

function getInitials(name) {
  if (!name) return "";
  const words = name.trim().split(" ");
  const initials = words.length >= 2 ? words[0][0] + words[1][0] : words[0][0];
  return initials.toUpperCase();
}

const DashboardBreadcrumb = ({ pathname }) => {
  const location = useLocation();
  const state = location.state || {};
  const pathSegments = pathname.split("/").filter(Boolean);

  // Always start with HOME
  const breadcrumbs = [
    {
      name: "HOME",
      href: "/dashboard",
      icon: <HomeIcon size={24} aria-hidden="true" />,
    },
  ];

  // Helper to get course/topic names from state/localStorage
  const getCrumbName = (segment, idx) => {
    // Course segment: /dashboard/courses/:courseId
    if (pathSegments[idx - 1] === "courses") {
      return (
        state.courseName ||
        localStorage.getItem(CURRENT_COURSE_NAME_KEY) ||
        decodeURIComponent(segment)
      );
    }
    // Topic segment: /dashboard/courses/:courseId/:topicId
    if (pathSegments[idx - 2] === "courses") {
      return state.topicName || decodeURIComponent(segment);
    }
    // Quiz history segment
    if (segment === "quiz-history") {
      return "Quiz History";
    }
    // Quiz segment
    if (segment === "quiz") {
      return "Quiz";
    }
    return decodeURIComponent(segment);
  };

  // Dynamically build crumbs for each segment after 'dashboard'
  let cumulativePath = "/dashboard";
  for (let i = 1; i < pathSegments.length; i++) {
    cumulativePath += `/${pathSegments[i]}`;
    breadcrumbs.push({
      name: getCrumbName(pathSegments[i], i),
      href: cumulativePath,
    });
  }

  return (
    <Breadcrumb>
      <BreadcrumbList className="px-3 py-2 text-sm capitalize">
        {breadcrumbs.map((crumb, index) => (
          <BreadcrumbItem key={crumb.href}>
            <BreadcrumbLink asChild>
              <Link
                to={crumb.href}
                state={
                  crumb.name && crumb.href.includes("/dashboard/courses/")
                    ? { courseName: crumb.name }
                    : undefined
                }
              >
                {crumb.icon}
                {crumb.icon ? (
                  <span className="sr-only">
                    {crumb.name.replaceAll("-", " ")}
                  </span>
                ) : (
                  <span>{crumb.name.replaceAll("-", " ")}</span>
                )}
              </Link>
            </BreadcrumbLink>
            {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
          </BreadcrumbItem>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export default function SeafarerDashboardLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [minutesLeft, setMinutesLeft] = useState(5);
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const snap = useSnapshot(authStore);
  let user = userDataFormat(snap.user) || {};

  // Check for incomplete profile on mount
  useEffect(() => {
    if (!user.name) {
      setShowProfileModal(true);
    } else {
      setShowProfileModal(false);
    }
  }, [user.name]);

  // Session Expiry Modal Logic
  useEffect(() => {
    const handleSessionExpiring = (event) => {
      if (event.detail && event.detail.minutesLeft) {
        setMinutesLeft(event.detail.minutesLeft);
        setShowExpiryModal(true);
      }
    };

    window.addEventListener("sessionExpiring", handleSessionExpiring);
    return () => {
      window.removeEventListener("sessionExpiring", handleSessionExpiring);
    };
  }, []);

  // Stay logged in handler
  const handleStayLoggedIn = () => {
    setShowExpiryModal(false);
  };

  const displayName =
    user.name ||
    user.fullName ||
    user.displayName ||
    user.email?.split("@")?.[0] ||
    "seafarer";

  // Hide sidebar/header for assessment window and certificate view
  const isAssessmentRoute = matchPath(
    "/dashboard/courses/:courseId/test/:quizId",
    location.pathname
  );
  const isCertificateRoute = matchPath(
    "/dashboard/certificate/:courseId",
    location.pathname
  );
  if (isAssessmentRoute || isCertificateRoute) {
    return <div className="min-h-screen bg-[#f6fafc]">{children}</div>;
  }

  return (
    <div className="flex min-h-screen w-full bg-[#f6fafc]">
      {/* Sidebar */}
      <Sidebar
        side="left"
        variant="sidebar"
        collapsible="offcanvas"
        className="w-70 bg-white p-2 shadow-xl font-helvetica text-[#4A4A4A]"
      >
        <div className="p-4 flex flex-col items-start justify-start gap-6">
          <div className="w-fit mx-auto">
            <img src={logo} alt="logo" className="w-26 h-auto" />
          </div>

          <nav className="flex flex-col gap-3 w-full">
            {sidebarItems.map((item) => {
              let isHighlighted = location.pathname
                .toLowerCase()
                .includes(item.href.toLowerCase());

              if (item.href === "/dashboard") {
                isHighlighted =
                  location.pathname === "/dashboard" ||
                  location.pathname.includes("/dashboard/courses");
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-4 py-2 rounded-full transition-all font-semibold text-base ${
                    isHighlighted
                      ? "bg-[#5D9CA4] text-black pointer-events-none"
                      : "text-[#4A4A4A] hover:bg-[#E6F2F4] hover:text-black"
                  }`}
                >
                  <span
                    className={`${
                      isHighlighted ? "text-white" : "text-[#4A4A4A]"
                    }`}
                  >
                    {item.icon}
                  </span>
                  <span
                    className={`${
                      isHighlighted ? "text-white font-bold" : "font-normal"
                    }`}
                  >
                    {item.name}
                  </span>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="mt-auto flex flex-col items-center gap-2 px-4 py-4">
          <img
            src={sponsors}
            alt="sponsors"
            className="w-full object-contain"
          />
          <img
            src={powered_colored}
            alt="powered"
            className="h-auto w-50 object-contain text-black"
          />
        </div>
      </Sidebar>

      {/* Main content */}
      <main className="flex min-h-[100dvh] min-w-0 flex-1 flex-col bg-[#F4F8F9] px-4 py-6 sm:px-6 lg:px-8">
        {/* Header : Search-Bar/Breadcrumbs and user popover */}
        <div className="flex flex-col gap-4 border-b border-[#E6F2F4] pb-4 sm:flex-row sm:items-center sm:justify-between">
          <DashboardBreadcrumb pathname={location.pathname} />

          {/* Notification and Profile Section */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            {/* Enhanced Notification Dropdown */}
            <NotificationDropdown variant="user" />

            {/* Profile Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="none"
                  className="flex items-center gap-2 px-8 py-6 rounded-md bg-transparent"
                >
                  <div className="">
                    {user.profileImage ? (
                      <div className="rounded-md bg-[var(--primary-font)] text-white w-10 h-10 flex items-center justify-center font-bold">
                        <img
                          src={user.profileImage}
                          alt="Profile"
                          className="w-full h-full object-cover object-center rounded-md"
                        />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-full text-sm">
                        <div className="w-full h-full flex items-center justify-center bg-[#5D93A5] rounded-full text-white font-medium text-lg">
                          {getInitials(user.name)}
                        </div>
                      </div>
                    )}
                  </div>
                  <span className="font-semibold text-[15px] font-seoge">
                    {displayName}
                  </span>
                  <ChevronDown className="w-5 h-5 group-data-[state=open]:rotate-180 transition-transform" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-[200px] h-fit font-helvetica bg-white"
              >
                <DropdownMenuItem asChild>
                  <Link
                    to="/dashboard/profile"
                    className="group cursor-pointer px-4 py-2 hover:underline-none w-full flex items-center justify-start gap-2 text-[0.9rem] text-[var(--default-font)] hover:!bg-[var(--primary-font)] hover:!text-white transition-all rounded-md"
                  >
                    <UserCircle2Icon className="!w-5 !h-5 transition-all group-hover:text-white" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    authStore.logout();
                    navigate("/");
                  }}
                  className="group cursor-pointer w-full flex items-center gap-2 px-4 py-2 justify-start !text-[0.9rem] text-[var(--default-font)] hover:!bg-[#FFCCCD] hover:!text-[#7F2B2C] transition-all"
                >
                  <LogOut className="!w-5 !h-5 transition-all group-hover:text-[#7F2B2C]" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Main content - will change per the tabs, this is the default tab */}
        <div className="relative w-full flex-1 overflow-auto bg-[#F4F8F9] px-2 py-4 sm:px-4 lg:px-6">
          {children}
        </div>
      </main>

      <SessionExpiryModal
        open={showExpiryModal}
        minutesLeft={minutesLeft}
        onStay={handleStayLoggedIn}
        onLogout={authStore.logout}
      />

      {showProfileModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 sm:p-4">
          <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-5xl max-h-[min(92vh,920px)] flex flex-col overflow-hidden">
            <ProfileCreationForm closeForm={() => setShowProfileModal(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
